import { useState, useRef } from "react";
import { api, resolveUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, ImageIcon } from "lucide-react";

export function SiteSettingsForm({ settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileRef = useRef(null);
  const faviconRef = useRef(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleUpload = (key, ref, label) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    setUploading(key);
    try {
      const res = await api.post("/admin/upload-logo", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set(key, res.data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading("");
      if (ref.current) ref.current.value = "";
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", form);
      toast.success("Site settings saved");
      onSaved(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, placeholder, multiline) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {multiline ? (
        <Textarea
          rows={3}
          value={form[key] || ""}
          onChange={(e) => set(key, e.target.value)}
          placeholder={placeholder}
          data-testid={`settings-${key}-input`}
        />
      ) : (
        <Input
          value={form[key] || ""}
          onChange={(e) => set(key, e.target.value)}
          placeholder={placeholder}
          data-testid={`settings-${key}-input`}
        />
      )}
    </div>
  );

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-2xl border border-stone-200/70 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-semibold text-stone-900">Group branding</h3>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Main group logo</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-40 items-center justify-center overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                {form.group_logo_url ? (
                  <img src={resolveUrl(form.group_logo_url)} alt="Group logo" className="h-full w-full object-contain p-2 mix-blend-multiply" data-testid="group-logo-preview" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-stone-300" />
                )}
              </div>
              <div>
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={handleUpload("group_logo_url", fileRef, "Group logo")} data-testid="group-logo-file-input" />
                <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => fileRef.current?.click()} disabled={uploading === "group_logo_url"} data-testid="group-logo-upload-button">
                  {uploading === "group_logo_url" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  Upload logo
                </Button>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Favicon (browser tab icon)</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
                {form.favicon_url ? (
                  <img src={resolveUrl(form.favicon_url)} alt="Favicon" className="h-full w-full object-contain p-2" data-testid="favicon-preview" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-stone-300" />
                )}
              </div>
              <div>
                <input ref={faviconRef} type="file" accept=".png,.svg,.ico,.webp" className="hidden" onChange={handleUpload("favicon_url", faviconRef, "Favicon")} data-testid="favicon-file-input" />
                <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => faviconRef.current?.click()} disabled={uploading === "favicon_url"} data-testid="favicon-upload-button">
                  {uploading === "favicon_url" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  Upload favicon
                </Button>
                <p className="mt-1.5 text-xs text-stone-400">Square PNG, SVG or ICO · 32×32 or larger</p>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Primary colour</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.primary_color || "#292524"} onChange={(e) => set("primary_color", e.target.value)} className="h-9 w-12 cursor-pointer rounded border border-stone-200" data-testid="settings-primary_color-input" />
              <Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="max-w-[140px] font-mono text-xs" />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200/70 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-semibold text-stone-900">Homepage content</h3>
        <div className="space-y-4">
          {field("headline", "Headline", "Specialist businesses. One connected group.")}
          {field("subheadline", "Supporting text", "", true)}
          {field("intro_paragraph", "Intro paragraph", "", true)}
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200/70 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-semibold text-stone-900">Footer</h3>
        <div className="space-y-4">
          {field("footer_text", "Footer name", "The CASS Group")}
          {field("footer_tagline", "Footer tagline", "Giving You More Than Numbers")}
          {field("footer_address", "Address", "1 Example Street\nTamworth\nB79 1AA", true)}
          {field("footer_email", "Contact email", "info@thecassgroup.co.uk")}
          {field("footer_phone", "Contact phone", "01827 123456")}
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200/70 bg-white p-6">
        <h3 className="mb-4 font-heading text-lg font-semibold text-stone-900">SEO</h3>
        <div className="space-y-4">
          {field("seo_title", "Page title", "")}
          {field("seo_description", "Meta description", "", true)}
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="rounded-lg" data-testid="settings-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
      </Button>
    </div>
  );
}
